import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import {
  derivedSpeakers,
  useTranscriptDraftStore,
} from "../store/transcriptDraftStore";
import {
  validateTranscriptDraft,
  TranscriptValidationResult,
} from "../utils/transcriptValidation";
import { MAX_WORD_CHANGE } from "../constants";

const EMPTY_VALIDATION: TranscriptValidationResult = {
  entries: {},
  allValid: true,
};

/**
 * Read-model for the transcript editor: the draft paragraphs, the (derived +
 * session) speaker list with reference counts, dirty + validation state, and
 * the single `canSave` gate. `duration` (when the video is loaded) bounds entry
 * end-time validation.
 */
export const useTranscriptDraft = (duration?: number) => {
  const draft = useTranscriptDraftStore((s) => s.draft);
  const original = useTranscriptDraftStore((s) => s.original);
  const sessionSpeakers = useTranscriptDraftStore((s) => s.sessionSpeakers);
  const rangeConfirmed = useTranscriptDraftStore((s) => s.rangeConfirmed);

  const paragraphs = draft?.paragraphs ?? [];

  const speakers = useMemo(() => {
    const merged = derivedSpeakers(draft);
    for (const s of sessionSpeakers) if (!merged.includes(s)) merged.push(s);
    return merged;
  }, [draft, sessionSpeakers]);

  /** label → number of entries currently assigned to it (0 for unused/session speakers). */
  const speakerCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const label of speakers) counts[label] = 0;
    for (const p of draft?.paragraphs ?? []) {
      const label = String(p.speaker ?? "");
      counts[label] = (counts[label] ?? 0) + 1;
    }
    return counts;
  }, [draft, speakers]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(original),
    [draft, original],
  );

  const validation = useMemo(() => {
    if (!draft) return EMPTY_VALIDATION;
    return validateTranscriptDraft({
      draft,
      original,
      rangeConfirmed: new Set(rangeConfirmed),
      maxWordChange: MAX_WORD_CHANGE,
      duration,
    });
  }, [draft, original, rangeConfirmed, duration]);

  const canSave = isDirty && validation.allValid;

  /** Original text of a sentence by entry _uid (for dirty highlighting). */
  const originalByUid = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const p of original?.paragraphs ?? []) {
      if (p._uid) map.set(p._uid, p.sentences.map((s) => s.text));
    }
    return map;
  }, [original]);

  return {
    draft,
    paragraphs,
    speakers,
    speakerCounts,
    isDirty,
    validation,
    canSave,
    originalByUid,
  };
};

/** Stable bundle of the store's mutators (function identities never change). */
export const useTranscriptActions = () =>
  useTranscriptDraftStore(
    useShallow((s) => ({
      initStore: s.initStore,
      destroyStore: s.destroyStore,
      discardChanges: s.discardChanges,
      editText: s.editText,
      reassignSpeaker: s.reassignSpeaker,
      verifySpeaker: s.verifySpeaker,
      deleteEntry: s.deleteEntry,
      deleteSentence: s.deleteSentence,
      addEntry: s.addEntry,
      setEntryRange: s.setEntryRange,
      splitSentenceToSpeaker: s.splitSentenceToSpeaker,
      addSpeaker: s.addSpeaker,
      renameSpeaker: s.renameSpeaker,
      renameSpeakers: s.renameSpeakers,
      deleteSpeaker: s.deleteSpeaker,
    })),
  );
